import type React from 'react';
import type { SiteStatus, BuilderTemplateId } from '../../hooks/use-builder-sites';
import { useBuilderSites } from '../../hooks/use-builder-sites';
import { BuilderLaunchLink } from '../../components/builder/builder-launch-link';
import { useCurrentProject } from '../../hooks/use-current-project';

export function BuilderPage(): React.ReactElement {
  const { projectId } = useCurrentProject();
  const { data, isLoading, isError, error } = useBuilderSites();
  const sites = data?.data ?? [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Website Builder</h1>
          <p className="mt-1 text-gray-400">Build and deploy token websites</p>
        </div>
        <BuilderLaunchLink
          {...(projectId ? { projectId } : {})}
          className="rounded-lg bg-[#c4f70e] px-4 py-2 font-medium text-black transition-colors hover:bg-[#d4ff2e]"
        >
          + New Site
        </BuilderLaunchLink>
      </div>

      {/* Error */}
      {isError && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error instanceof Error ? error.message : 'Failed to load sites'}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <SiteCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && sites.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.02] py-20 text-center">
          <GlobeIcon className="mb-4 h-12 w-12 text-gray-600" />
          <h3 className="text-lg font-semibold text-white">No sites yet</h3>
          <p className="mt-1 text-sm text-gray-500">Build your first token website.</p>
          <BuilderLaunchLink
            {...(projectId ? { projectId } : {})}
            className="mt-6 rounded-lg bg-[#c4f70e] px-4 py-2 font-medium text-black transition-colors hover:bg-[#d4ff2e]"
          >
            Open Builder
          </BuilderLaunchLink>
        </div>
      )}

      {/* Sites grid */}
      {!isLoading && !isError && sites.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => (
            <SiteCard key={site.id} site={site} {...(projectId ? { projectId } : {})} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Site Card ────────────────────────────────────────────────────────────────

interface SiteCardProps {
  projectId?: string;
  site: {
    id: string;
    subdomain: string;
    status: SiteStatus;
    templateId: string;
    deploymentUrl?: string;
    createdAt: string;
  };
}

function SiteCard({ site, projectId }: SiteCardProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-800 bg-gray-900 p-5 transition-colors hover:border-gray-700">
      {/* Top row: subdomain + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-white">{site.subdomain}</p>
          <p className="mt-0.5 truncate text-xs text-gray-500">{formatDate(site.createdAt)}</p>
        </div>
        <StatusBadge status={site.status} />
      </div>

      {/* Template badge */}
      <TemplateBadge templateId={site.templateId} />

      {/* Actions */}
      <div className="flex items-center gap-2 border-t border-white/[0.06] pt-3">
        <BuilderLaunchLink
          {...(projectId ? { projectId } : {})}
          siteId={site.id}
          className="flex-1 rounded-lg border border-white/[0.08] py-1.5 text-center text-sm text-gray-400 transition-colors hover:border-white/[0.16] hover:text-white"
        >
          Open in Builder
        </BuilderLaunchLink>
        {site.status === 'PUBLISHED' && site.deploymentUrl && (
          <a
            href={site.deploymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 rounded-lg border border-[#c4f70e]/20 bg-[#c4f70e]/5 px-3 py-1.5 text-sm text-[#c4f70e] transition-colors hover:bg-[#c4f70e]/10"
          >
            View Live
            <ExternalLinkIcon className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: SiteStatus }): React.ReactElement {
  const styles: Record<SiteStatus, string> = {
    DRAFT: 'bg-gray-500/10 text-gray-400',
    GENERATING: 'bg-amber-500/10 text-amber-400',
    READY: 'bg-lime-500/10 text-lime-400',
    PUBLISHED: 'bg-[#c4f70e]/10 text-[#c4f70e] shadow-[0_0_8px_rgba(196,247,14,0.2)]',
    FAILED: 'bg-red-500/10 text-red-400',
  };

  return (
    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

// ─── Template Badge ───────────────────────────────────────────────────────────

function TemplateBadge({ templateId }: { templateId: string }): React.ReactElement {
  const label =
    (templateId as BuilderTemplateId) === 'degen'
      ? 'Degen'
      : (templateId as BuilderTemplateId) === 'clean'
        ? 'Clean'
        : (templateId as BuilderTemplateId) === 'meme'
          ? 'Meme'
          : templateId;

  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <LayoutIcon className="h-3 w-3" />
      {label} template
    </span>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SiteCardSkeleton(): React.ReactElement {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="h-4 w-32 animate-pulse rounded bg-white/[0.06]" />
          <div className="h-3 w-20 animate-pulse rounded bg-white/[0.04]" />
        </div>
        <div className="h-5 w-16 animate-pulse rounded-full bg-white/[0.06]" />
      </div>
      <div className="h-3 w-24 animate-pulse rounded bg-white/[0.04]" />
      <div className="flex gap-2 border-t border-white/[0.06] pt-3">
        <div className="h-8 flex-1 animate-pulse rounded-lg bg-white/[0.04]" />
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function GlobeIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
      />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

function LayoutIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z"
      />
    </svg>
  );
}
